// Phase 36 Batch S — Settings → Connected Accounts section.
//
// Shows: 
//   • Email auth row — always present (every user has an email)
//   • Google auth row — "linked" with profile picture if user has linked
//     it, or a "Connect Google" CTA if not yet linked.
//
// Disconnect Google button is disabled (with tooltip explaining why) when
// Google is the ONLY auth provider — the user would lock themselves out.
// The backend ALSO enforces this in /api/auth/me/disconnect-google so
// the UI check is just a guard against an accidental tap.

import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Check, Loader2 } from "lucide-react";

// Inline Google "G" mark in the official 4 colours. Avoids adding the
// `react-icons` dependency for one icon.
function GoogleG({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 19 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8.1L6 32.7C9.2 39.4 16.1 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.2 5.2C40.9 35.7 44 30.3 44 24c0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

function startGoogleConnect() {
  // Same redirect target the login + signup buttons use. After
  // returning, AuthCallback.jsx will either log the user in afresh
  // (if they weren't logged in) OR — if they came back already
  // authenticated — link the Google account to the current user
  // (server-side match by email).
  const redirect = encodeURIComponent(`${window.location.origin}/auth/callback`);
  window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
}

export default function ConnectedAccountsCard() {
  const { user, refresh } = useAuth() || {};
  const [busy, setBusy] = useState(false);
  if (!user) return null;

  const providers = user.auth_providers || ["email"];
  const hasEmail = providers.includes("email");
  const hasGoogle = providers.includes("google");
  // The disconnect-Google button is disabled when Google is the
  // user's ONLY auth provider — disconnecting would lock them out.
  // Backend enforces this rule too via /api/auth/me/disconnect-google.
  const canDisconnect = hasGoogle && hasEmail;

  const onDisconnect = async () => {
    if (!canDisconnect || busy) return;
    setBusy(true);
    try {
      await api.post("/auth/me/disconnect-google");
      if (typeof refresh === "function") await refresh();
      toast.success("Google account disconnected.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't disconnect Google. Please try again.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="connected-accounts-card"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <div className="mb-4">
        <h2 className="text-base font-semibold text-slate-900">Connected accounts</h2>
        <p className="mt-1 text-sm text-slate-500">
          Choose how you sign in to RunIdea. Linking more than one provider keeps
          your account recoverable if you lose access to either.
        </p>
      </div>

      {/* Email row — always shown. */}
      <div
        data-testid="provider-row-email"
        className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-slate-600 ring-1 ring-slate-200">
            <Mail className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">Email & password</p>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
        </div>
        {hasEmail ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
            <Check className="h-3 w-3" /> Linked
          </span>
        ) : (
          <span className="text-xs text-slate-400">Not set</span>
        )}
      </div>

      {/* Google row — Connect/Disconnect button depending on link state. */}
      <div
        data-testid="provider-row-google"
        className="mt-3 flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white ring-1 ring-slate-200">
            {hasGoogle && user.google_picture_url ? (
              <img
                src={user.google_picture_url}
                alt=""
                className="h-7 w-7 rounded-md object-cover"
                referrerPolicy="no-referrer"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <GoogleG className="h-4 w-4" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900">Google</p>
            <p className="text-xs text-slate-500">
              {hasGoogle
                ? "Continue with Google on any device."
                : "One-tap sign-in with your Google account."}
            </p>
          </div>
        </div>
        {hasGoogle ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canDisconnect || busy}
            onClick={onDisconnect}
            data-testid="disconnect-google-btn"
            title={
              canDisconnect
                ? "Disconnect Google from this account."
                : "Set a password on this account before disconnecting Google — otherwise you'd lock yourself out."
            }
            className="border-slate-200 text-slate-700 hover:bg-white"
          >
            {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            Disconnect
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={startGoogleConnect}
            data-testid="connect-google-btn"
            className="border-slate-300 bg-white text-slate-700 hover:border-indigo-300 hover:text-indigo-700"
          >
            <GoogleG className="mr-1.5 h-3.5 w-3.5" /> Connect Google
          </Button>
        )}
      </div>

      {/* Footnote when Google is the only provider — explains why
          Disconnect is disabled. */}
      {hasGoogle && !canDisconnect ? (
        <p
          data-testid="single-provider-lockout-warning"
          className="mt-3 text-xs text-amber-700"
        >
          Google is your only sign-in method. Set an email password first to
          enable Disconnect.
        </p>
      ) : null}
    </div>
  );
}
